import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Simple deep merge utility to merge partial updates into existing data
function mergeDeep(target: any, source: any) {
  for (const key of Object.keys(source)) {
    const srcVal = (source as any)[key];
    if (
      srcVal &&
      typeof srcVal === 'object' &&
      !Array.isArray(srcVal)
    ) {
      if (!(target as any)[key]) {
        (target as any)[key] = {};
      }
      mergeDeep((target as any)[key], srcVal);
    } else {
      (target as any)[key] = srcVal;
    }
  }
}

// Define the shape of the GraphQL data we'll store
export interface GraphQLCompanyData {
  id: string;
  slug: string;
  employees?: string | null;
  category?: string | null;
  logo_url?: string | null;
  description?: string | null;
  url?: string | null;
  scraper?: string | null;
  tags?: Array<{
    id: string;
    name: string;
    description?: string | null;
  }> | null;
  pages?: Array<{
    id: string;
    subslug: string | null;
    pricing_page_url?: string | null;
    thresholds?: Array<{
      id: string;
      display_name: string;
      sample_values?: string[] | null;
    }> | null;
    quarters?: Array<{
      quarter: string;
      version: string;
      ids: string[];
      screenshot_url?: string | null;
      verified?: boolean | null;
    }> | null;
    scrapes?: Array<{
      version: string;
      quarter: string;
      screenshot_url?: string | null;
      markdown_url?: string | null;
      html_url?: string | null;
      verified?: boolean | null;
    }> | null;
    plans?: Array<any> | null;
  }> | null;
}

interface GraphQLState {
  data: GraphQLCompanyData | null;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: GraphQLState = {
  data: null,
  loading: false,
  error: null,
  lastFetched: null,
};

const graphqlSlice = createSlice({
  name: 'graphql',
  initialState,
  reducers: {
    setGraphQLData: (state, action: PayloadAction<GraphQLCompanyData | null>) => {
      state.data = action.payload;
      state.lastFetched = action.payload ? Date.now() : null;
      console.log('[graphQL Redux] Data stored:', action.payload);
    },
    /**
     * Merge partial GraphQL data into the existing store.
     * Useful after mutations that only return updated fields.
     */
    mergeGraphQLData: (state, action: PayloadAction<Partial<GraphQLCompanyData>>) => {
      if (!state.data) {
        // If no data exists yet, simply assign it
        state.data = action.payload as GraphQLCompanyData;
      } else {
        mergeDeep(state.data, action.payload);
      }
      state.lastFetched = Date.now();
    },
    setGraphQLLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setGraphQLError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearGraphQLData: (state) => {
      state.data = null;
      state.lastFetched = null;
    },
  },
});

export const {
  setGraphQLData,
  mergeGraphQLData,
  setGraphQLLoading,
  setGraphQLError,
  clearGraphQLData
} = graphqlSlice.actions;

export default graphqlSlice.reducer;
